import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Link2, X } from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../services/api";
import { formatInr } from "./payroll/runPayrollUtils";

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

/** Pay months (YYYY-MM) for a FY + quarter. FY like "2024-25". */
export function monthsForQuarter(financialYear, quarter) {
  const startY = parseInt(String(financialYear || "").split("-")[0], 10);
  if (!startY || !quarter) return [];
  const map = {
    Q1: [`${startY}-04`, `${startY}-05`, `${startY}-06`],
    Q2: [`${startY}-07`, `${startY}-08`, `${startY}-09`],
    Q3: [`${startY}-10`, `${startY}-11`, `${startY}-12`],
    Q4: [`${startY + 1}-01`, `${startY + 1}-02`, `${startY + 1}-03`],
  };
  return map[quarter] || [];
}

function monthLabel(ym) {
  if (!ym) return "—";
  const [y, m] = String(ym).split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const mi = Number(m) - 1;
  return names[mi] ? `${names[mi]} ${y}` : ym;
}

/**
 * Link month-wise TDS deductions to a challan.
 * Requires explicit period (quarter + month) before loading lines.
 */
export default function LinkDeductionsModal({
  open,
  onClose,
  darkMode = false,
  challan,
  filingId,
  financialYear,
  quarter,
  onLinked,
}) {
  const [step, setStep] = useState("period"); // period | lines
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [lines, setLines] = useState([]);
  const [selected, setSelected] = useState({});
  const [nameFilter, setNameFilter] = useState("");
  const [qtrFilter, setQtrFilter] = useState(quarter || "Q1");
  const [monthFilter, setMonthFilter] = useState(""); // "" = all months in quarter

  const inputCls = `mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
    darkMode ? "border-[#555] bg-[#2a2a2a] text-white" : "border-gray-300 bg-white"
  }`;

  const challanBalance = Number(challan?.balanceAvailable ?? challan?.tdsAmount ?? 0);
  const periodMonths = useMemo(
    () => monthsForQuarter(financialYear, qtrFilter),
    [financialYear, qtrFilter]
  );

  useEffect(() => {
    if (!open) return;
    setStep("period");
    setQtrFilter(quarter || "Q1");
    setMonthFilter("");
    setLines([]);
    setSelected({});
    setNameFilter("");
  }, [open, financialYear, quarter, challan?.id]);

  const load = async () => {
    if (!financialYear || !qtrFilter) {
      toast.error("Select financial year and quarter");
      return;
    }
    setLoading(true);
    try {
      const res = await payrollOpsService.getTdsLinkableDeductions({
        financialYear,
        quarter: qtrFilter,
        filingId: filingId || undefined,
        section: challan?.section || "192",
        payMonth: monthFilter || undefined,
      });
      const data = res?.data || res || {};
      setLines(data.lines || []);
      setSelected({});
      setStep("lines");
      if (!(data.lines || []).length) {
        toast("No unlinked deductions for this period", { icon: "ℹ️" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load deductions");
    }
    setLoading(false);
  };

  const filtered = useMemo(() => {
    if (!nameFilter.trim()) return lines;
    const s = nameFilter.toLowerCase();
    return lines.filter(
      (l) =>
        String(l.name || "").toLowerCase().includes(s) ||
        String(l.pan || "").toLowerCase().includes(s)
    );
  }, [lines, nameFilter]);

  const selectedTotal = useMemo(
    () => Object.values(selected).reduce((sum, amt) => sum + (Number(amt) || 0), 0),
    [selected]
  );

  const remainingOnChallan = Math.round((challanBalance - selectedTotal) * 100) / 100;

  const lineKey = (l) => `${l.employeeId}|${l.payMonth}|${l.deducteeId || ""}`;

  const toggleLine = (l) => {
    const key = lineKey(l);
    setSelected((prev) => {
      if (prev[key] != null) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: Number(l.remainingToLink) || 0 };
    });
  };

  const selectAll = () => {
    let budget = challanBalance;
    const next = {};
    for (const l of filtered) {
      if (budget <= 0.01) break;
      const want = Number(l.remainingToLink) || 0;
      const take = Math.min(want, budget);
      if (take > 0.01) {
        next[lineKey(l)] = take;
        budget = Math.round((budget - take) * 100) / 100;
      }
    }
    setSelected(next);
  };

  const clearAll = () => setSelected({});

  const handleLink = async () => {
    const selections = filtered
      .filter((l) => selected[lineKey(l)] != null)
      .map((l) => ({
        employeeId: l.employeeId,
        deducteeId: l.deducteeId,
        payMonth: l.payMonth,
        amount: Number(selected[lineKey(l)]) || 0,
      }))
      .filter((s) => s.amount > 0);

    if (!selections.length) {
      toast.error("Select at least one deduction line");
      return;
    }
    if (selectedTotal > challanBalance + 0.01) {
      toast.error("Selected amount exceeds challan balance");
      return;
    }

    setLinking(true);
    try {
      const res = await payrollOpsService.linkTdsChallanDeductions(challan.id, {
        filingId,
        quarter: qtrFilter,
        selections,
      });
      toast.success(`Linked ${formatInr(selectedTotal)} for ${qtrFilter}${monthFilter ? ` · ${monthLabel(monthFilter)}` : ""}`);
      onLinked?.(res?.data || res);
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Link failed");
    }
    setLinking(false);
  };

  if (!open || typeof document === "undefined") return null;

  const periodSummary = `${financialYear} · ${qtrFilter}${
    monthFilter ? ` · ${monthLabel(monthFilter)}` : " · All months"
  }`;

  return createPortal(
    <div
      className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/50 p-4"
      onClick={() => onClose(false)}
    >
      <div
        className={`flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl shadow-xl ${
          darkMode ? "bg-[#1e1e1e] text-white" : "bg-white"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Link Deductions"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-base font-bold">Link deductions to challan</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Serial {challan?.challanSerialNumber || challan?.id} · BSR {challan?.bsrCode || "—"} ·
              Balance{" "}
              <span className="font-semibold text-[hsl(var(--ds-fg))]">
                {formatInr(challanBalance)}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-[#333]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Step 1 — Period */}
        {step === "period" && (
          <div className="min-h-0 flex-1 overflow-auto px-5 py-5">
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              Choose the <strong>period</strong> whose TDS you want to attach to this challan.
              Linking only applies to the quarter and month(s) you select — not the full year.
            </p>
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-900">
              <strong>Split across challans:</strong> e.g. April TDS ₹7,000 paid as ₹5,000 + ₹2,000 —
              open <strong>Link</strong> on the first challan, select April, enter ₹5,000 in{" "}
              <em>Link amt</em>, then Link the remaining ₹2,000 on the second challan for the same month.
            </div>

            <div className="space-y-5">
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Financial year
                </p>
                <p className="rounded-lg border border-dashed px-3 py-2 text-sm font-medium">
                  {financialYear || "—"}
                </p>
              </div>

              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Quarter
                </p>
                <div className="flex flex-wrap gap-2">
                  {QUARTERS.map((q) => {
                    const isFiling = q === (quarter || "Q1");
                    const active = qtrFilter === q;
                    return (
                      <button
                        key={q}
                        type="button"
                        onClick={() => {
                          setQtrFilter(q);
                          setMonthFilter("");
                        }}
                        className={`rounded-lg border px-4 py-2 text-sm font-semibold ${
                          active
                            ? "border-redhalo-red bg-redhalo-red text-white"
                            : darkMode
                              ? "border-[#555] hover:bg-[#2a2a2a]"
                              : "border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {q}
                        {isFiling && !active && (
                          <span className="ml-1 text-[10px] font-normal opacity-80">(this return)</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {qtrFilter !== quarter && (
                  <p className="mt-2 text-[11px] text-amber-700">
                    This return is for {quarter}. You can still link another quarter’s deductions to
                    this challan’s balance if needed.
                  </p>
                )}
              </div>

              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Month within {qtrFilter}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setMonthFilter("")}
                    className={`rounded-lg border px-3 py-2 text-xs font-semibold ${
                      !monthFilter
                        ? "border-redhalo-red bg-redhalo-red/10 text-redhalo-red"
                        : darkMode
                          ? "border-[#555]"
                          : "border-gray-300"
                    }`}
                  >
                    All months in {qtrFilter}
                  </button>
                  {periodMonths.map((ym) => (
                    <button
                      key={ym}
                      type="button"
                      onClick={() => setMonthFilter(ym)}
                      className={`rounded-lg border px-3 py-2 text-xs font-semibold ${
                        monthFilter === ym
                          ? "border-redhalo-red bg-redhalo-red/10 text-redhalo-red"
                          : darkMode
                            ? "border-[#555]"
                            : "border-gray-300"
                      }`}
                    >
                      {monthLabel(ym)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — Lines */}
        {step === "lines" && (
          <>
            <div className="flex flex-wrap items-center gap-3 border-b px-5 py-3">
              <div className="text-xs">
                <span className="text-gray-500">Period:</span>{" "}
                <span className="font-semibold">{periodSummary}</span>
              </div>
              <button
                type="button"
                onClick={() => setStep("period")}
                className={`rounded-lg border px-2.5 py-1 text-[11px] font-semibold ${
                  darkMode ? "border-[#555]" : "border-gray-300"
                }`}
              >
                Change period
              </button>
              <label className="ml-auto text-xs font-medium">
                Filter name / PAN
                <input
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  className={`${inputCls} min-w-[10rem]`}
                  placeholder="Search…"
                />
              </label>
            </div>

            <div className="min-h-0 flex-1 overflow-auto px-5 py-3">
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="animate-spin text-gray-400" />
                </div>
              ) : (
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]">
                    <tr>
                      <th className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={
                            filtered.length > 0 &&
                            filtered.every((l) => selected[lineKey(l)] != null)
                          }
                          onChange={(e) => (e.target.checked ? selectAll() : clearAll())}
                        />
                      </th>
                      <th className="px-2 py-2">Paid month</th>
                      <th className="px-2 py-2">Name</th>
                      <th className="px-2 py-2">PAN</th>
                      <th className="px-2 py-2 text-right">Tax Ded.</th>
                      <th className="px-2 py-2 text-right">Remaining</th>
                      <th className="px-2 py-2 text-right">Link amt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l) => {
                      const key = lineKey(l);
                      const isOn = selected[key] != null;
                      return (
                        <tr
                          key={key}
                          className={`border-t ${isOn ? "bg-redhalo-red/5" : ""} ${
                            darkMode ? "border-[#333]" : "border-gray-100"
                          }`}
                        >
                          <td className="px-2 py-1.5">
                            <input type="checkbox" checked={isOn} onChange={() => toggleLine(l)} />
                          </td>
                          <td className="px-2 py-1.5">
                            {l.isQuarterResidual || !l.payMonth
                              ? "Quarter balance"
                              : monthLabel(l.payMonth)}
                          </td>
                          <td className="px-2 py-1.5 font-medium">{l.name}</td>
                          <td className="px-2 py-1.5 font-mono text-[11px]">{l.pan || "—"}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {formatInr(l.taxDeducted)}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {formatInr(l.remainingToLink)}
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            {isOn ? (
                              <input
                                type="number"
                                className={`${inputCls} !mt-0 w-24 text-right`}
                                value={selected[key]}
                                max={l.remainingToLink}
                                min={0}
                                step="0.01"
                                onChange={(e) =>
                                  setSelected((p) => ({
                                    ...p,
                                    [key]: Number(e.target.value) || 0,
                                  }))
                                }
                              />
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!filtered.length && (
                      <tr>
                        <td colSpan={7} className="px-2 py-8 text-center text-gray-400">
                          No unlinked deduction lines for this period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        <div
          className={`flex flex-wrap items-center gap-3 border-t px-5 py-3 ${
            darkMode ? "border-[#333] bg-[#242424]" : "border-gray-100 bg-gray-50"
          }`}
        >
          {step === "period" ? (
            <>
              <p className="text-xs text-gray-500">
                Selected: <strong className="text-[hsl(var(--ds-fg))]">{periodSummary}</strong>
              </p>
              <div className="ml-auto flex gap-2">
                <button
                  type="button"
                  onClick={() => onClose(false)}
                  className={`rounded-lg border px-4 py-2 text-sm ${
                    darkMode ? "border-[#555]" : "border-gray-300"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={load}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-redhalo-red px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                  Load deductions
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-xs">
                <span className="text-gray-500">Challan balance:</span>{" "}
                <span className="font-bold tabular-nums">{formatInr(challanBalance)}</span>
              </div>
              <div className="text-xs">
                <span className="text-gray-500">Link total:</span>{" "}
                <span className="font-bold tabular-nums">{formatInr(selectedTotal)}</span>
              </div>
              <div className="text-xs">
                <span className="text-gray-500">Remaining:</span>{" "}
                <span
                  className={`font-bold tabular-nums ${
                    remainingOnChallan < -0.01 ? "text-red-600" : "text-emerald-700"
                  }`}
                >
                  {formatInr(remainingOnChallan)}
                </span>
              </div>
              <div className="ml-auto flex gap-2">
                <button
                  type="button"
                  onClick={() => onClose(false)}
                  className={`rounded-lg border px-4 py-2 text-sm ${
                    darkMode ? "border-[#555]" : "border-gray-300"
                  }`}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleLink}
                  disabled={linking || selectedTotal <= 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-redhalo-red px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {linking ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                  Link selected
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
